import { Controller } from "@hotwired/stimulus"

const META_KEY = 'autoboost_meta_sidebar_open'
const TIKTOK_KEY = 'autoboost_tiktok_sidebar_open'
const REPORTING_KEY = 'autoboost_reporting_sidebar_open'
const ACCOUNT_KEY = 'autoboost_account_sidebar_open'

export default class extends Controller {
  static targets = ["sidebar", "overlay", "metaSection", "metaChevron", "tiktokSection", "tiktokChevron", "reportingSection", "reportingChevron", "accountSection", "accountChevron"]
  static values = {
    open: { type: Boolean, default: false }
  }

  connect() {
    console.log(`[sidebar] connect: open=${this.openValue}, has{sidebar,meta,reporting,account}=${this.hasSidebarTarget}/${this.hasMetaSectionTarget}/${this.hasReportingSectionTarget}/${this.hasAccountSectionTarget}`)
    if (!this.hasSidebarTarget) {
      console.warn(`[sidebar] connect: no sidebar target — skipping init`)
      return
    }
    this.checkScreenSize()
    // Store the bound handler — removeEventListener with a fresh .bind()
    // removes nothing, leaking a resize listener per Turbo navigation.
    this.resizeHandler = this.checkScreenSize.bind(this)
    window.addEventListener('resize', this.resizeHandler)

    const path = window.location.pathname

    if (this.hasMetaSectionTarget) {
      if (path.startsWith('/meta') || localStorage.getItem(META_KEY) === 'true') {
        this._expandMeta()
      } else {
        this._collapseMeta()
      }
    }
    if (this.hasTiktokSectionTarget) {
      if (path.startsWith('/tiktok') || localStorage.getItem(TIKTOK_KEY) === 'true') {
        this._expandTiktok()
      } else {
        this._collapseTiktok()
      }
    }
    if (this.hasReportingSectionTarget) {
      const onReportingPage = ['/reports', '/exports', '/export_templates', '/data_exports', '/automations', '/activity']
        .some((p) => path.startsWith(p))
      if (onReportingPage || localStorage.getItem(REPORTING_KEY) === 'true') {
        this._expandReporting()
      } else {
        this._collapseReporting()
      }
    }
    if (this.hasAccountSectionTarget) {
      const onAccountPage = path.startsWith('/account') || path.startsWith('/connectors') ||
        ['/billing', '/invoices', '/subscription', '/settings'].includes(path)
      if (onAccountPage || localStorage.getItem(ACCOUNT_KEY) === 'true') {
        this._expandAccount()
      } else {
        this._collapseAccount()
      }
    }
  }

  disconnect() {
    if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler)
  }

  toggle() {
    this.openValue = !this.openValue
    this.updateVisibility()
  }

  open() {
    this.openValue = true
    this.updateVisibility()
  }

  close() {
    this.openValue = false
    this.updateVisibility()
  }

  // ---- Generic collapsible section helper ----
  _toggleSection(section, chevron, key) {
    const hidden = section.classList.contains('hidden')
    if (hidden) {
      section.classList.remove('hidden')
      if (chevron) chevron.classList.add('rotate-180')
      this._scrollExpandedIntoView(section)
      localStorage.setItem(key, 'true')
    } else {
      section.classList.add('hidden')
      if (chevron) chevron.classList.remove('rotate-180')
      localStorage.setItem(key, 'false')
    }
  }

  _expand(section, chevron) {
    section.classList.remove('hidden')
    if (chevron) chevron.classList.add('rotate-180')
    this._scrollExpandedIntoView(section)
  }

  _collapse(section, chevron) {
    section.classList.add('hidden')
    if (chevron) chevron.classList.remove('rotate-180')
  }

  toggleMeta() { this._toggleSection(this.metaSectionTarget, this.hasMetaChevronTarget ? this.metaChevronTarget : null, META_KEY) }
  _expandMeta() { this._expand(this.metaSectionTarget, this.hasMetaChevronTarget ? this.metaChevronTarget : null) }
  _collapseMeta() { this._collapse(this.metaSectionTarget, this.hasMetaChevronTarget ? this.metaChevronTarget : null) }

  toggleTiktok() { this._toggleSection(this.tiktokSectionTarget, this.hasTiktokChevronTarget ? this.tiktokChevronTarget : null, TIKTOK_KEY) }
  _expandTiktok() { this._expand(this.tiktokSectionTarget, this.hasTiktokChevronTarget ? this.tiktokChevronTarget : null) }
  _collapseTiktok() { this._collapse(this.tiktokSectionTarget, this.hasTiktokChevronTarget ? this.tiktokChevronTarget : null) }

  toggleReporting() { this._toggleSection(this.reportingSectionTarget, this.hasReportingChevronTarget ? this.reportingChevronTarget : null, REPORTING_KEY) }
  _expandReporting() { this._expand(this.reportingSectionTarget, this.hasReportingChevronTarget ? this.reportingChevronTarget : null) }
  _collapseReporting() { this._collapse(this.reportingSectionTarget, this.hasReportingChevronTarget ? this.reportingChevronTarget : null) }

  toggleAccount() { this._toggleSection(this.accountSectionTarget, this.hasAccountChevronTarget ? this.accountChevronTarget : null, ACCOUNT_KEY) }
  _expandAccount() { this._expand(this.accountSectionTarget, this.hasAccountChevronTarget ? this.accountChevronTarget : null) }
  _collapseAccount() { this._collapse(this.accountSectionTarget, this.hasAccountChevronTarget ? this.accountChevronTarget : null) }

  // Bring a freshly-expanded sidebar dropdown into view on small viewports.
  // On a mobile viewport an expanded sub-section can render below the visible
  // portion of the sidebar's overflow-y-auto column — the chevron flips, the
  // items render, but the user sees no change. rAF lets the browser commit
  // the class change before we measure + scroll.
  _scrollExpandedIntoView(target) {
    if (!target) return
    requestAnimationFrame(() => {
      try {
        target.scrollIntoView({ block: 'end', behavior: 'smooth' })
      } catch (e) {
        // Older Safari without ScrollIntoViewOptions support — safe to ignore.
      }
    })
  }

  updateVisibility() {
    if (!this.hasSidebarTarget) return
    if (this.openValue) {
      this.sidebarTarget.classList.remove('-translate-x-full')
      this.sidebarTarget.classList.add('translate-x-0')
      if (this.hasOverlayTarget) this.overlayTarget.classList.remove('hidden')
      document.body.style.overflow = 'hidden'
    } else {
      this.sidebarTarget.classList.add('-translate-x-full')
      this.sidebarTarget.classList.remove('translate-x-0')
      if (this.hasOverlayTarget) this.overlayTarget.classList.add('hidden')
      document.body.style.overflow = ''
    }
  }

  checkScreenSize() {
    if (!this.hasSidebarTarget) return
    if (window.innerWidth >= 1024) {
      this.sidebarTarget.classList.remove('-translate-x-full')
      this.sidebarTarget.classList.add('translate-x-0')
      if (this.hasOverlayTarget) this.overlayTarget.classList.add('hidden')
      document.body.style.overflow = ''
    } else if (!this.openValue) {
      this.sidebarTarget.classList.add('-translate-x-full')
      this.sidebarTarget.classList.remove('translate-x-0')
    }
  }
}
